import React, { useEffect, useMemo, useState } from "react";
import { agentApi } from "../../services/agentApi";
import { sessionStepLabel } from "./sessionLabels";

const INTENT_OPTIONS = [
  { key: "not_now", label: "Agora não" },
  { key: "interested", label: "Interessado" },
  { key: "objection", label: "Objeção" },
];

const DEFAULT_RULES = {
  enabled: true,
  defaultBusinessDays: 3,
  maxAttempts: 3,
  closingCalendarDays: 5,
  timezone: "America/Sao_Paulo",
  byStep: {},
  byIntent: {},
};

function normalize(raw) {
  if (!raw || Array.isArray(raw) || typeof raw !== "object") {
    return { ...DEFAULT_RULES, byStep: {}, byIntent: {} };
  }
  return {
    enabled: raw.enabled !== false,
    defaultBusinessDays: Number(raw.defaultBusinessDays) || 3,
    maxAttempts: Number(raw.maxAttempts) || 3,
    closingCalendarDays: Number(raw.closingCalendarDays) || 5,
    timezone: raw.timezone || "America/Sao_Paulo",
    byStep: { ...(raw.byStep || {}) },
    byIntent: { ...(raw.byIntent || {}) },
  };
}

function stepCodesFromPlaybook(playbook) {
  const steps = playbook?.steps || [];
  const wait = steps
    .map((s) => s.code)
    .filter((code) => code?.startsWith("WAIT_") || code === "HANDLE_OBJECTION" || code === "NURTURE_LATER" || code === "OFFER_MEETING");
  return [...new Set(wait)];
}

export default function PlaybookFollowUpEditor({ playbook, onSaved, readOnly = false }) {
  const [rules, setRules] = useState(() => normalize(playbook.followUpRules));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const stepCodes = useMemo(() => stepCodesFromPlaybook(playbook), [playbook]);

  useEffect(() => {
    setRules(normalize(playbook.followUpRules));
  }, [playbook.id, playbook.version]);

  function patch(partial) {
    setSaved(false);
    setRules((prev) => ({ ...prev, ...partial }));
  }

  function patchStep(code, field, value) {
    setSaved(false);
    setRules((prev) => ({
      ...prev,
      byStep: {
        ...prev.byStep,
        [code]: {
          ...(prev.byStep[code] || {}),
          [field]: value,
        },
      },
    }));
  }

  function patchIntent(key, field, value) {
    setSaved(false);
    setRules((prev) => ({
      ...prev,
      byIntent: {
        ...prev.byIntent,
        [key]: {
          ...(prev.byIntent[key] || {}),
          [field]: value,
        },
      },
    }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        enabled: rules.enabled,
        defaultBusinessDays: Number(rules.defaultBusinessDays) || 3,
        maxAttempts: Number(rules.maxAttempts) || 3,
        closingCalendarDays: Number(rules.closingCalendarDays) || 5,
        timezone: rules.timezone || "America/Sao_Paulo",
        byStep: Object.fromEntries(
          Object.entries(rules.byStep || {})
            .filter(([, v]) => v && (v.businessDays != null || v.maxAttempts != null || v.promptHint))
            .map(([code, v]) => [
              code,
              {
                ...(v.businessDays != null && v.businessDays !== "" ? { businessDays: Number(v.businessDays) } : {}),
                ...(v.maxAttempts != null && v.maxAttempts !== "" ? { maxAttempts: Number(v.maxAttempts) } : {}),
                ...(v.promptHint ? { promptHint: v.promptHint } : {}),
              },
            ]),
        ),
        byIntent: Object.fromEntries(
          Object.entries(rules.byIntent || {})
            .filter(([, v]) => v && (v.businessDays != null || v.maxAttempts != null))
            .map(([key, v]) => [
              key,
              {
                ...(v.businessDays != null && v.businessDays !== "" ? { businessDays: Number(v.businessDays) } : {}),
                ...(v.maxAttempts != null && v.maxAttempts !== "" ? { maxAttempts: Number(v.maxAttempts) } : {}),
              },
            ]),
        ),
      };
      await agentApi.playbooks.patch(playbook.id, { followUpRules: payload });
      setSaved(true);
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  const inputClass = `w-24 px-2 py-1 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 ${readOnly ? "cursor-default bg-gray-50" : ""}`;

  return (
    <div className="space-y-6 max-w-4xl">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
      )}
      {saved && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md text-sm">
          Cadência salva (versão incrementada).
        </div>
      )}

      <p className="text-sm text-gray-600">
        Se o lead não responder, o agente envia até o máximo de tentativas, em dias úteis (seg–sex, sem feriados nacionais).
        O intervalo padrão é de 3 dias úteis entre cada toque. Depois da última tentativa, espera os dias corridos indicados, envia uma mensagem final com o contato da Wone e encerra a conversa.
      </p>

      <label className={`inline-flex items-center gap-2 text-sm text-gray-800 ${readOnly ? "" : "cursor-pointer"}`}>
        <input
          type="checkbox"
          checked={rules.enabled}
          disabled={readOnly}
          onChange={(e) => patch({ enabled: e.target.checked })}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        Retentativa automática habilitada
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dias úteis padrão</label>
          <input
            type="number"
            min={1}
            max={30}
            value={rules.defaultBusinessDays}
            readOnly={readOnly}
            onChange={(e) => patch({ defaultBusinessDays: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de tentativas</label>
          <input
            type="number"
            min={1}
            max={10}
            value={rules.maxAttempts}
            readOnly={readOnly}
            onChange={(e) => patch({ maxAttempts: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dias até encerrar</label>
          <input
            type="number"
            min={1}
            max={30}
            value={rules.closingCalendarDays}
            readOnly={readOnly}
            onChange={(e) => patch({ closingCalendarDays: e.target.value })}
            className={inputClass}
          />
          <p className="text-[11px] text-gray-400 mt-1">Corridos, após a última tentativa</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fuso</label>
          <input
            type="text"
            value={rules.timezone}
            readOnly={readOnly}
            onChange={(e) => patch({ timezone: e.target.value })}
            className={`w-full px-2 py-1 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 ${readOnly ? "cursor-default bg-gray-50" : ""}`}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Por etapa da conversa</h3>
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Etapa</th>
                <th className="text-left px-3 py-2 font-medium">Dias úteis</th>
                <th className="text-left px-3 py-2 font-medium">Tentativas</th>
                <th className="text-left px-3 py-2 font-medium">Orientação do toque</th>
              </tr>
            </thead>
            <tbody>
              {stepCodes.map((code) => {
                const row = rules.byStep[code] || {};
                return (
                  <tr key={code} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {sessionStepLabel(code)}
                      <div className="text-[11px] text-gray-400 font-mono">{code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        max={30}
                        placeholder={String(rules.defaultBusinessDays)}
                        value={row.businessDays ?? ""}
                        readOnly={readOnly}
                        onChange={(e) => patchStep(code, "businessDays", e.target.value)}
                        className={inputClass}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={1}
                        max={10}
                        placeholder={String(rules.maxAttempts)}
                        value={row.maxAttempts ?? ""}
                        readOnly={readOnly}
                        onChange={(e) => patchStep(code, "maxAttempts", e.target.value)}
                        className={inputClass}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <textarea
                        rows={2}
                        value={row.promptHint || ""}
                        readOnly={readOnly}
                        onChange={(e) => patchStep(code, "promptHint", e.target.value)}
                        className={`w-full px-2 py-1 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 ${readOnly ? "cursor-default bg-gray-50" : ""}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Por contexto da última resposta</h3>
        <p className="text-xs text-gray-500 mb-2">
          Quando preenchido, o prazo da intenção prevalece sobre o da etapa (ex.: “agora não” espera mais; “interessado” cobra mais cedo).
        </p>
        <div className="space-y-2">
          {INTENT_OPTIONS.map(({ key, label }) => {
            const row = rules.byIntent[key] || {};
            return (
              <div key={key} className="flex flex-wrap items-center gap-3 p-3 rounded-md border border-gray-200 bg-white">
                <span className="w-28 text-sm font-medium text-gray-800">{label}</span>
                <label className="text-xs text-gray-500 flex items-center gap-1">
                  Dias úteis
                  <input
                    type="number"
                    min={1}
                    max={30}
                    placeholder="—"
                    value={row.businessDays ?? ""}
                    readOnly={readOnly}
                    onChange={(e) => patchIntent(key, "businessDays", e.target.value)}
                    className={inputClass}
                  />
                </label>
                <label className="text-xs text-gray-500 flex items-center gap-1">
                  Tentativas
                  <input
                    type="number"
                    min={1}
                    max={10}
                    placeholder="—"
                    value={row.maxAttempts ?? ""}
                    readOnly={readOnly}
                    onChange={(e) => patchIntent(key, "maxAttempts", e.target.value)}
                    className={inputClass}
                  />
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {!readOnly && (
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md font-medium transition-colors"
        >
          {saving ? "Salvando..." : "Salvar cadência"}
        </button>
      )}
    </div>
  );
}
